import { useEffect, useState } from 'react';
import { useSelector } from '@/redux';
import { STUCK_MUTATION_TIMEOUT_MS } from '@/redux/middleware/basketMutationGuard';
import { isBasketMutating } from '@/redux/orderStatus/reducer';

/**
 * `isBasketMutating` for UI gating, bounded so a stuck mutation cannot brick checkout.
 *
 * The selector itself has to fail closed — `basketMutationGuard` uses it to decide
 * whether a second write would race the one in flight, and guessing wrong there is
 * the money-affecting bug (TEAM-14696). Disabling a control has the opposite risk
 * profile: several paths legitimately end with `type` still `UPDATING` and nothing
 * to clear it, and `validateOrderSaga`'s generic error branch is the common one —
 * it deliberately does not dispatch `orderPending()` unless the caller passed
 * `rearmOnFailure`, so that a failed validate keeps Place Order gated (TEAM-14666).
 * The mount-time validate does not pass it. Gate every control on the raw selector
 * and one plain 400 from `validateBasket` leaves the diner staring at a dead
 * checkout page for the rest of the session, with no dispatch left that could
 * recover it.
 *
 * So: after `STUCK_MUTATION_TIMEOUT_MS` the controls come back, and the write the
 * diner then attempts is forced straight through rather than deferred for another
 * window, because the middleware measures its own window from when the mutation
 * *started*.
 *
 * The two clocks are not identical, and the difference is in the safe direction.
 * This one starts when a mounted component first observes `mutating`, which for a
 * checkout mounting mid-init is later than the dispatch that set it — and a chained
 * write (`tipAddSaga`'s `orderPending()` immediately followed by `validateOrder`)
 * can be batched by React into a single observed value, so the hook may not see the
 * dip that resets the middleware's clock. Both cases make the hook release *later*
 * than the middleware, never earlier, so the controls never re-enable while the
 * middleware would still defer what they dispatch.
 *
 * This does not re-arm Place Order. That is gated on
 * `orderStatus.status === ORDER_PENDING` (submitOrder/index.tsx), which a failed
 * validate leaves at `VALIDATING_ORDER`, so TEAM-14666's fail-closed submit
 * behaviour is untouched — the diner can fix their basket and retry, not submit an
 * unvalidated one.
 */
export function useBasketMutating(): boolean {
  const mutating = useSelector(isBasketMutating);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    setStuck(false);
    if (!mutating) {
      return;
    }
    const timer = setTimeout(() => setStuck(true), STUCK_MUTATION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [mutating]);

  return mutating && !stuck;
}
